// JOH World — fighter-sprites: alle poses worden afgeleid van de officiële
// aangeleverde karakter-afbeeldingen (transparant). Push-up frames zijn aparte
// aangeleverde pixel-art poses (down 198x91 / up 179x122).
import { getFighter, fighterReady, GLOVE_FRONT, PAL } from "./engine";

const ASPECT = 244 / 175; // h = w * ASPECT

const pushupUpImg = new Image();
pushupUpImg.src = "/johworld/pushup_up.png";
const pushupDownImg = new Image();
pushupDownImg.src = "/johworld/pushup_down.png";
const duckImg = new Image();
duckImg.src = "/johworld/fighter_duck.png"; // 273x254 — officiële buk-pose (aangeleverde asset)

// Teken een pixel-bokshandschoen (originele JOH World-vorm).
export function drawGlove(ctx, x, y, s = 1, flip = false) {
  ctx.save();
  ctx.translate(Math.round(x), Math.round(y));
  if (flip) ctx.scale(-1, 1);
  ctx.fillStyle = PAL.darkRed;
  ctx.fillRect(0, 1 * s, 8 * s, 6 * s); // outline
  ctx.fillStyle = PAL.red;
  ctx.fillRect(1 * s, 0, 6 * s, 6 * s); // bol
  ctx.fillRect(0, 2 * s, 8 * s, 4 * s);
  ctx.fillStyle = "#FF6A5E";
  ctx.fillRect(2 * s, 1 * s, 2 * s, 2 * s); // highlight
  ctx.fillStyle = PAL.cream;
  ctx.fillRect(-2 * s, 3 * s, 2 * s, 3 * s); // polsband
  ctx.restore();
}

// Impact-sterretje voor cross-klappen.
export function drawImpact(ctx, x, y, s = 1, color = PAL.yellow) {
  ctx.fillStyle = color;
  ctx.fillRect(x - 4 * s, y - 1 * s, 8 * s, 2 * s);
  ctx.fillRect(x - 1 * s, y - 4 * s, 2 * s, 8 * s);
  ctx.fillRect(x - 3 * s, y - 3 * s, 2 * s, 2 * s);
  ctx.fillRect(x + 1 * s, y - 3 * s, 2 * s, 2 * s);
  ctx.fillRect(x - 3 * s, y + 1 * s, 2 * s, 2 * s);
  ctx.fillRect(x + 1 * s, y + 1 * s, 2 * s, 2 * s);
}

/**
 * Teken de fighter. (x, yFeet) = positie van de voeten in wereld-coords.
 * pose: idle | walk | jump | duck | jab | cross | hit | victory | pushupUp | pushupDown
 * t: tijd in seconden (voor frame-animatie), phase: aanvalsvoortgang 0..1
 */
export function drawFighter(ctx, x, yFeet, w, pose, t, { flip = false, phase = 0, blink = false } = {}) {
  if (!fighterReady()) return;
  const img = getFighter();
  const h = w * ASPECT;

  // Invulnerability-blink: klassiek retro-geknipper
  if (blink && Math.floor(t * 14) % 2 === 0) return;

  ctx.save();
  ctx.translate(Math.round(x), Math.round(yFeet));

  let scaleY = 1;
  let rot = 0;
  let dx = 0;
  let dy = -h; // voeten als anker

  switch (pose) {
    case "walk": {
      const f = Math.floor(t * 8) % 2; // 2 frames
      dy += f === 0 ? 0 : -2;
      rot = f === 0 ? 0.035 : -0.035;
      break;
    }
    case "jump":
      rot = -0.06;
      break;
    case "duck": {
      // Officiële buk-pose: diep door de knieën, ver voorover, hoofd omlaag.
      // Vasthouden = gebukt blijven; loslaten = staande vechthouding (gestuurd door input.down).
      if (duckImg.complete && duckImg.naturalWidth > 0) {
        const aD = duckImg.naturalHeight / duckImg.naturalWidth;
        const wD = w * 1.08; // vergelijkbare schaal als de staande sprite
        if (flip) ctx.scale(-1, 1);
        ctx.drawImage(duckImg, Math.round(-wD / 2), Math.round(-wD * aD), Math.round(wD), Math.round(wD * aD));
      }
      ctx.restore();
      return;
    }
    case "jab": {
      const ext = phase < 0.5 ? 6 : 10; // 2 frames
      dx += 3;
      rot = 0.07;
      drawBody();
      // body-local coords: drawBody heeft flip (scale(-1,1)) al toegepast → handschoen spiegelt automatisch mee
      const gx = (GLOVE_FRONT.x - 0.5) * w + ext;
      const gy = -h + GLOVE_FRONT.y * h;
      drawGlove(ctx, gx, gy, 1, flip);
      ctx.restore();
      return;
    }
    case "cross": {
      const ext = phase < 0.4 ? 9 : 17; // 2-3 frames
      dx += 5;
      rot = 0.13;
      drawBody();
      const gx = (GLOVE_FRONT.x - 0.5) * w + ext;
      const gy = -h + GLOVE_FRONT.y * h;
      drawGlove(ctx, gx, gy, 1.1, flip);
      if (phase > 0.35 && phase < 0.8) drawImpact(ctx, gx + 8, gy + 3, 1);
      ctx.restore();
      return;
    }
    case "hit":
      rot = -0.16;
      dx -= 3;
      ctx.filter = "brightness(1.7) sepia(1) hue-rotate(-50deg) saturate(4)"; // rode flits
      break;
    case "victory": {
      const f = Math.floor(t * 5) % 2;
      dy += f === 0 ? -5 : 0;
      rot = f === 0 ? 0.1 : -0.06;
      break;
    }
    case "pushupUp":
    case "pushupDown": {
      // Officiële push-up pixel-art frames (aangeleverde asset), in een loop
      const img2 = pose === "pushupUp" ? pushupUpImg : pushupDownImg;
      if (img2.complete && img2.naturalWidth > 0) {
        const a2 = img2.naturalHeight / img2.naturalWidth;
        const w2 = w * 1.75; // horizontale houding is breder dan de staande sprite
        if (flip) ctx.scale(-1, 1);
        ctx.drawImage(img2, Math.round(-w2 / 2), Math.round(-w2 * a2), Math.round(w2), Math.round(w2 * a2));
      }
      ctx.restore();
      return;
    }
    default: {
      // idle: zachte adem-bob
      dy += Math.round(Math.sin(t * 3.2) * 1.2);
    }
  }

  drawBody();
  ctx.restore();

  function drawBody() {
    ctx.rotate(rot);
    if (flip) ctx.scale(-1, 1);
    ctx.drawImage(img, Math.round(dx - w / 2), Math.round(dy), Math.round(w), Math.round(h * scaleY));
  }
}
